import type { BadgeVariant } from '@/components/ui/badge';
import { DateTime } from 'luxon';

/**
 * The invoice status state machine, extracted from `invoice-status.tsx` so
 * the datawarehouse Avier & Fakturor report can reuse it on DW documents
 * (snake_case, no matrix metas). The branch ORDER is the contract — it
 * mirrors what users see in the invoicing list. Change the two files
 * together: this core and the `getInvoiceStatus` mapper are one machine.
 */
export interface InvoiceStatusInput {
  /** Legacy: invoiceValuesMeta.total_value ?? 0. DW: Σ rows net+vat. */
  totalValue: number;
  closedTime: string | null | undefined;
  /** debt_invoice_counts.unclosed_debt_invoices */
  unclosedDebtInvoices: number | null | undefined;
  /** debt_invoice_counts.kinds['1'] — debt collection invoices sent. */
  sentDebtCount: number | null | undefined;
  /** debt_invoice_counts.kinds['0'] — reminders sent. */
  sentReminderCount: number | null | undefined;
  automaticDebtInvoicingPaused: boolean | null | undefined;
  attestedDate: string | null | undefined;
  distributionDate: string | null | undefined;
  dueDate: string | null | undefined;
  /** Legacy: invoiceComputedMeta.payments_sum. DW: invoice.paid_amount. */
  paymentsSum: number | null | undefined;
  invoiceNumber: number | string | null | undefined;
  ocrNumber: string | null | undefined;
  /** Legacy: invoiceComputedMeta.creditation.is_credited. DW: not synced. */
  isCredited: boolean | null | undefined;
  /**
   * Σ|source_invoice_total_value| of the crediting invoices, null when any
   * of them is unknown. The credit invoices' own totals, not the amount
   * applied to this invoice — an upper bound on what was credited here.
   */
  creditSourceTotal: number | null | undefined;
}

export interface InvoiceStatusResult {
  text: string;
  badgeVariant: BadgeVariant['variant'];
  additionalInfo?: string;
}

/**
 * One day's grace after the due date before an invoice counts as overdue.
 * Shared with the invoice timeline so the rail and the badge flip together.
 */
export function isPastDue(
  dueDate: string | null | undefined,
  now: DateTime = DateTime.now()
) {
  return dueDate ? now > DateTime.fromISO(dueDate).plus({ days: 1 }) : false;
}

function getCreditStatus(input: InvoiceStatusInput): InvoiceStatusResult {
  if (!input.attestedDate) {
    return { text: 'Kredit, ej attesterad', badgeVariant: 'yellow' };
  }
  if (input.closedTime) {
    return { text: 'Kredit, nyttjad', badgeVariant: 'outline' };
  }
  if (input.paymentsSum && input.paymentsSum < 0) {
    return { text: 'Kredit, delvis nyttjad', badgeVariant: 'cyan' };
  }
  return { text: 'Kredit, onyttjad', badgeVariant: 'orange' };
}

/**
 * "Delkrediterad" is certain: the credit applied here can't exceed the
 * credit invoices' totals. "Krediterad" can overstate it when one credit
 * invoice is spread over several invoices.
 */
function getCreditedLabel(input: InvoiceStatusInput) {
  if (!input.isCredited) return null;
  if (
    input.creditSourceTotal != null &&
    Math.round(input.creditSourceTotal * 100) <
      Math.round(input.totalValue * 100)
  ) {
    return 'Delkrediterad';
  }
  return 'Krediterad';
}

/** " (A, b)" from the parts that are set, the first one capitalised. */
function toSuffix(...parts: (string | false | null)[]) {
  const text = parts.filter(Boolean).join(', ');
  return text ? ` (${text.charAt(0).toUpperCase()}${text.slice(1)})` : '';
}

export function getInvoiceStatusCore(
  input: InvoiceStatusInput,
  now: DateTime = DateTime.now()
): InvoiceStatusResult {
  if (input.totalValue < 0) {
    return getCreditStatus(input);
  }

  if (input.closedTime) {
    const hasOpenDebt =
      !!input.unclosedDebtInvoices && input.unclosedDebtInvoices > 0;
    return {
      text: `Slutbetald${toSuffix(getCreditedLabel(input), hasOpenDebt && 'öppna påminnelser/krav')}`,
      badgeVariant: hasOpenDebt ? 'orange' : 'green',
    };
  }

  const isPaused = !!input.automaticDebtInvoicingPaused;
  // An open invoice still has an amount left, so it is at most partly
  // credited, whatever the credit invoices total.
  const credited = input.isCredited ? 'Delkrediterad' : null;
  const suffix = toSuffix(credited, isPaused && 'pausad');
  const overdueVariant: BadgeVariant['variant'] = isPaused
    ? 'light-gray'
    : 'destructive';

  // A reminder or krav can only be created on an attested invoice past its
  // due date, and the due date is locked after attest — so a sent one always
  // means the invoice is overdue, and the prefix cannot be wrong.
  if (input.sentDebtCount !== undefined && (input.sentDebtCount ?? 0) > 0) {
    return {
      text: `Förfallen, kravhantering${suffix}`,
      badgeVariant: overdueVariant,
    };
  }

  if (
    input.sentReminderCount !== undefined &&
    (input.sentReminderCount ?? 0) > 0
  ) {
    return {
      text: `Förfallen, påminnelse skickad${suffix}`,
      badgeVariant: overdueVariant,
    };
  }

  const pastDuePlusOne = isPastDue(input.dueDate, now);

  if (pastDuePlusOne && !input.closedTime && input.attestedDate) {
    return { text: `Förfallen${suffix}`, badgeVariant: overdueVariant };
  }

  if (input.distributionDate && input.attestedDate && !input.closedTime) {
    if (input.paymentsSum && input.paymentsSum > 0) {
      return {
        text: `Skickad, delbetald${suffix}`,
        badgeVariant: isPaused ? 'light-gray' : 'cyan',
      };
    }
    return {
      text: `Skickad${suffix}`,
      badgeVariant: isPaused ? 'light-gray' : 'blue',
    };
  }

  if (!input.attestedDate) {
    return {
      text: `Ej attesterad${toSuffix(credited)}`,
      badgeVariant: 'yellow',
    };
  }

  if (input.invoiceNumber && input.ocrNumber) {
    return { text: `Attesterad${toSuffix(credited)}`, badgeVariant: 'green' };
  }

  return {
    text: `Misslyckad attestering${toSuffix(credited)}`,
    badgeVariant: 'destructive',
    additionalInfo:
      'Attesteringen av denna avi/faktura misslyckades. Försök igen.',
  };
}
